import React, { useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, Cell, LabelList,
} from 'recharts';
import { useToolData } from '../hooks/useToolData';
import api from '../../../lib/api';

/**
 * Custom X-axis tick that preserves Hebrew character order on Safari/iOS.
 *
 * Safari's SVG <text> rendering of Hebrew inside a `dir="rtl"` page flips
 * characters (שבת → תבש). CSS hints (`unicode-bidi: plaintext`) don't fix
 * it reliably there. The robust fix is to detect Hebrew runs and reverse
 * their character order so the visual order after Safari flips them is the
 * original. On Chrome/Firefox we render them as-is.
 */
const HEBREW_RE = /[\u0590-\u05FF]/;
const isSafari = typeof navigator !== 'undefined'
  && /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');

function fixHebrewForSafari(text: string): string {
  if (!isSafari || !text || !HEBREW_RE.test(text)) return text;
  // Reverse char order in each whitespace-separated word, keep the word
  // order itself — Safari will then visually flip them back into the
  // correct reading order.
  return text.split(/(\s+)/).map(chunk =>
    HEBREW_RE.test(chunk) ? chunk.split('').reverse().join('') : chunk
  ).reverse().join('');
}

const RTLSafeTick: React.FC<any> = ({ x, y, payload, fontSize = 11, fill = '#6b7280' }) => (
  <g transform={`translate(${x},${y})`}>
    <text x={0} y={12} textAnchor="middle" fontSize={fontSize} fill={fill}>
      {fixHebrewForSafari(String(payload?.value ?? ''))}
    </text>
  </g>
);

/**
 * DynamicChart renders a bar/line/area chart fed by a Tool's submissions.
 *
 * Two data sources:
 *   - 'timeseries'   → x-axis is a time bucket (day/week/month)
 *   - 'distribution' → x-axis is field values (useful for bar charts of checkbox/select distributions)
 */
export interface DynamicChartProps {
  props: Record<string, any>;
  style: Record<string, any>;
  baseStyle: React.CSSProperties;
}

export const DynamicChart: React.FC<DynamicChartProps> = ({ props: p, style: s, baseStyle }) => {
  const chartType: 'bar' | 'line' | 'area' = p.chartType || 'bar';
  const source: 'timeseries' | 'distribution' = p.source || 'distribution';
  const distributionMode: 'single' | 'multi' = p.distributionMode || 'single';

  const hasDistributionFields =
    (distributionMode === 'single' && !!p.fieldId) ||
    (distributionMode === 'multi' && Array.isArray(p.fieldIds) && p.fieldIds.length > 0);
  const isConfigured = (p.toolId || p.toolSlug) && (source === 'timeseries' || hasDistributionFields);

  // Build spec per source type
  const spec: Record<string, any> = {};
  if (source === 'timeseries') {
    spec.mode = p.metricMode || 'submissions_count';
    if (p.metricFieldId) spec.fieldId = p.metricFieldId;
    if (p.option) spec.option = p.option;
    if (Array.isArray(p.conditions) && p.conditions.length) spec.conditions = p.conditions;
    spec.bucket = p.bucket || 'day';
    spec.rangeDays = p.rangeDays || 30;
  } else {
    if (distributionMode === 'multi') {
      spec.fieldIds = p.fieldIds;
      if (p.fieldLabels) spec.fieldLabels = p.fieldLabels;
    } else {
      spec.fieldId = p.fieldId;
      if (p.fieldLabels) spec.fieldLabels = p.fieldLabels;
    }
    // Only pass sumFieldId when it looks like a real field id (guards against
    // stale garbage values like "1" saved from older editor versions).
    if (p.sumFieldId && /^f/.test(p.sumFieldId)) spec.sumFieldId = p.sumFieldId;
    if (p.limit) spec.limit = p.limit;
    if (Array.isArray(p.conditions) && p.conditions.length) spec.conditions = p.conditions;
    if (p.timeWindowDays) spec.timeWindowDays = p.timeWindowDays;
  }

  const { data, loading, error } = useToolData<{ data: Array<{ key: string; value: number }> }>({
    kind: source,
    toolId: p.toolId || undefined,
    toolSlug: p.toolSlug || undefined,
    spec,
    pollSeconds: p.livePolling || 0,
    skip: !isConfigured,
  });

  const wrapStyle: React.CSSProperties = {
    ...baseStyle,
    display: 'flex', flexDirection: 'column',
    height: '100%', width: '100%',
  };

  if (!isConfigured) {
    const missing = !p.toolId && !p.toolSlug
      ? 'בחר טופס ב"מקור נתונים"'
      : source === 'distribution' && distributionMode === 'single' ? 'בחר שדה ב"נתונים לגרף"'
      : source === 'distribution' && distributionMode === 'multi' ? 'בחר לפחות שדה אחד ב"נתונים לגרף"'
      : 'השלם את הגדרת הגרף';
    return (
      <div style={{ ...wrapStyle, alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: 16 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
        <div>{missing}</div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div style={{ ...wrapStyle, alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
        טוען נתונים...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...wrapStyle, alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: 13 }}>
        שגיאה בטעינת נתונים
      </div>
    );
  }

  const rawFromBackend = data?.data || [];
  // Fill in missing options with value=0 so EVERY option always shows a bar,
  // not only the ones that received picks. Applies to single-field mode
  // where we know the full list of options from the schema.
  const rawChartData = (() => {
    if (source !== 'distribution' || distributionMode !== 'single') return rawFromBackend;
    if (!Array.isArray(p.fieldOptions) || !p.fieldOptions.length) return rawFromBackend;
    const byKey = new Map<string, number>();
    for (const d of rawFromBackend) byKey.set(String(d.key), d.value);
    return p.fieldOptions.map((opt: string) => ({
      key: String(opt),
      value: byKey.get(String(opt)) || 0,
    }));
  })();
  // Sort bars based on user preference. For timeseries we leave the order
  // alone (chronological), so only sort in distribution mode.
  const chartData = (() => {
    if (source !== 'distribution') return rawChartData;
    const sortMode = p.sortMode || 'value_desc';
    if (sortMode === 'none') {
      // "Sort by field order":
      // - multi mode   → backend already returns in `fieldIds` order
      // - single mode  → backend sorts by value, so re-sort here to match
      //                  the schema's option order (`p.fieldOptions`)
      if (distributionMode === 'single' && Array.isArray(p.fieldOptions) && p.fieldOptions.length) {
        const orderMap = new Map<string, number>(p.fieldOptions.map((o: string, i: number) => [String(o), i]));
        const sorted = [...rawChartData].sort((a, b) => {
          const ai = orderMap.has(a.key) ? orderMap.get(a.key)! : Number.MAX_SAFE_INTEGER;
          const bi = orderMap.has(b.key) ? orderMap.get(b.key)! : Number.MAX_SAFE_INTEGER;
          return ai - bi;
        });
        // Hebrew / RTL reading order: the first option should appear on the
        // RIGHT of the chart. Recharts draws data left-to-right, so reverse.
        return sorted.reverse();
      }
      // Multi-field "none" — fieldIds preserved order; reverse for RTL too.
      if (distributionMode === 'multi') return [...rawChartData].reverse();
      return rawChartData;
    }
    const copy = [...rawChartData];
    switch (sortMode) {
      case 'value_desc': return copy.sort((a, b) => b.value - a.value);
      case 'value_asc':  return copy.sort((a, b) => a.value - b.value);
      case 'key_asc':    return copy.sort((a, b) => String(a.key).localeCompare(String(b.key), 'he'));
      case 'key_desc':   return copy.sort((a, b) => String(b.key).localeCompare(String(a.key), 'he'));
      default: return rawChartData;
    }
  })();
  const allZero = chartData.length > 0 && chartData.every(d => !d.value);
  // If we're showing a single-field chart with seeded options, render the
  // zero-bars even when no submissions exist yet — the user explicitly asked
  // for every option to always appear. Only fall back to the empty-state for
  // truly empty results (no options to seed from).
  const hasSeededOptions =
    source === 'distribution' && distributionMode === 'single' &&
    Array.isArray(p.fieldOptions) && p.fieldOptions.length > 0;

  if (!loading && chartData.length === 0 && !hasSeededOptions) {
    return <EmptyChartState
      chartData={chartData}
      allZero={allZero}
      toolId={p.toolId} toolSlug={p.toolSlug}
      spec={spec} wrapStyle={wrapStyle}
    />;
  }
  const color = p.color || s.bgColor || '#2563eb';
  const colors: string[] = Array.isArray(p.colors) && p.colors.length ? p.colors : [color];

  // Y-axis domain. When startAtZero is true (default), force 0 as the minimum
  // so bar heights are comparable; otherwise let Recharts auto-pick.
  const yDomain: [number | string, number | string] = p.startAtZero !== false
    ? [0, 'auto']
    : ['auto', 'auto'];
  const barRadius = p.barRadius !== undefined ? p.barRadius : 4;
  const barSize = p.barWidth && p.barWidth > 0 ? p.barWidth : undefined;
  const barCategoryGap = p.barCategoryGap !== undefined ? `${p.barCategoryGap}%` : '20%';
  // Render the numeric value above each bar / point when enabled
  const showValues = !!p.showValues;
  const valueFontSize = p.valueFontSize || 12;
  const valueColor = p.valueColor || s.textColor || '#111827';

  const chartInner = (() => {
    if (chartType === 'line') {
      return (
        <LineChart data={chartData} margin={{ top: showValues ? 24 : 8, right: 16, left: 0, bottom: 0 }}>
          {p.showGrid !== false && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          <XAxis dataKey="key" tick={<RTLSafeTick />} />
          <YAxis tick={{ fontSize: 11 }} domain={yDomain} hide={p.showYAxis === false} />
          <Tooltip />
          {p.showLegend !== false && <Legend />}
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={p.lineWidth || 2} dot={{ r: 3 }}>
            {showValues && <LabelList dataKey="value" position="top" style={{ fontSize: valueFontSize, fill: valueColor, fontWeight: 600 }} />}
          </Line>
        </LineChart>
      );
    }
    if (chartType === 'area') {
      const gradId = `grad-${Math.random().toString(36).slice(2, 8)}`;
      return (
        <AreaChart data={chartData} margin={{ top: showValues ? 24 : 8, right: 16, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.7} />
              <stop offset="100%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          {p.showGrid !== false && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
          <XAxis dataKey="key" tick={<RTLSafeTick />} />
          <YAxis tick={{ fontSize: 11 }} domain={yDomain} hide={p.showYAxis === false} />
          <Tooltip />
          {p.showLegend !== false && <Legend />}
          <Area type="monotone" dataKey="value" stroke={color} fill={`url(#${gradId})`} strokeWidth={p.lineWidth || 2}>
            {showValues && <LabelList dataKey="value" position="top" style={{ fontSize: valueFontSize, fill: valueColor, fontWeight: 600 }} />}
          </Area>
        </AreaChart>
      );
    }
    // bar (default)
    return (
      <BarChart data={chartData} margin={{ top: showValues ? 24 : 8, right: 16, left: 0, bottom: 0 }} barCategoryGap={barCategoryGap}>
        {p.showGrid !== false && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
        <XAxis dataKey="key" tick={<RTLSafeTick />} />
        <YAxis tick={{ fontSize: 11 }} domain={yDomain} hide={p.showYAxis === false} />
        <Tooltip />
        {p.showLegend !== false && <Legend />}
        <Bar dataKey="value" radius={[barRadius, barRadius, 0, 0]} barSize={barSize}>
          {chartData.map((_, i) => (
            <Cell key={i} fill={colors[i % colors.length]} />
          ))}
          {showValues && <LabelList dataKey="value" position="top" style={{ fontSize: valueFontSize, fill: valueColor, fontWeight: 600 }} />}
        </Bar>
      </BarChart>
    );
  })();

  return (
    <div style={wrapStyle}>
      {p.title && (
        <div style={{ fontSize: 16, fontWeight: 'bold', color: s.textColor || '#111827', marginBottom: 8 }}>
          {p.title}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartInner}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Empty/all-zero chart state with a "Diagnose" button that re-fetches the
 * distribution endpoint with `debug: true` and shows the raw submission keys,
 * so the user can see EXACTLY what's stored vs. what we're looking up.
 */
const EmptyChartState: React.FC<{
  chartData: Array<{ key: string; value: number }>;
  allZero: boolean;
  toolId?: string;
  toolSlug?: string;
  spec: Record<string, any>;
  wrapStyle: React.CSSProperties;
}> = ({ chartData, allZero, toolId, toolSlug, spec, wrapStyle }) => {
  const [diag, setDiag] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const runDiag = async () => {
    if (!toolId) { setDiag({ err: 'אין toolId — הפעל בעורך' }); return; }
    setBusy(true);
    try {
      const r = await api.post(`/analytics/tools/${toolId}/distribution`, { ...spec, debug: true });
      setDiag(r.data.diag);
    } catch (e: any) {
      setDiag({ err: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ ...wrapStyle, alignItems: 'stretch', justifyContent: 'flex-start', color: '#9ca3af', fontSize: 12, padding: 12, overflow: 'auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 28 }}>📊</div>
        <div style={{ fontWeight: 600, color: '#6b7280' }}>
          {allZero ? 'כל הערכים 0 — בדוק את מבנה הנתונים' : 'אין נתונים להצגה'}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.5, marginBottom: 8, textAlign: 'center' }}>
        {allZero ? 'הגרף מחזיר 0 לכל עמודה. ייתכן שה-id של השדות לא מתאים למה שמאוחסן בהגשות.' : 'ייתכן שאין הגשות לטופס, או שהשדה שנבחר ריק בכל ההגשות.'}
      </div>
      <button
        onClick={runDiag}
        disabled={busy}
        style={{
          alignSelf: 'center', padding: '6px 14px', borderRadius: 6,
          background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
          fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', marginBottom: 8,
        }}
      >
        {busy ? 'טוען...' : '🔍 הצג מידע אבחון'}
      </button>
      {diag && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, fontSize: 11, color: '#374151', maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}>
          {diag.err ? (
            <div style={{ color: '#dc2626' }}>שגיאה: {diag.err}</div>
          ) : (
            <>
              <div><b>Total submissions:</b> {diag.submissions}</div>
              <div style={{ marginTop: 6 }}><b>Sample keys found in data:</b></div>
              {(diag.sample || []).slice(0, 2).map((s: any, i: number) => (
                <div key={i} style={{ marginTop: 4 }}>
                  <div>#{i + 1}: {s.keys?.join(', ') || '(empty)'}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', wordBreak: 'break-all' }}>
                    {JSON.stringify(s.data).slice(0, 200)}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 6 }}><b>We're looking for:</b></div>
              <div style={{ fontSize: 10 }}>
                fieldIds: {JSON.stringify(diag.spec?.fieldIds || diag.spec?.fieldId)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DynamicChart;
